import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Button,
  ClickAwayListener,
  Divider,
  MenuItem,
  Paper,
  Popper,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import StyleConstants from '../../../core/styles.constants';
import { editorTheme, sectionHeadingSx } from '../utils/editor-theme';
import {
  DESK_GROUP_KINDS,
  MAXIMUM_CUSTOM_DESKS,
  MAXIMUM_SPACING_METRES,
  seatCountOf,
  shapeOf,
  type DeskGroupKind,
  type DeskGroupShape,
} from '../utils/desk-groups';
import type { DeskFootprint } from '../utils/desk-symbols';
import { isUsableScale, type Scale } from '../utils/measurement';
import type { DeskIcons } from '../utils/mutations';
import DeskIconField from './desk-icon-field.component';

interface Props {
  readonly anchorEl: HTMLElement | null;
  readonly scale: Scale;
  readonly kind: DeskGroupKind;
  /** Rows and columns for a custom group. Ignored while a preset is chosen. */
  readonly custom: DeskGroupShape;
  readonly footprint: DeskFootprint;
  readonly rotationDegrees: number;
  /** Between desks side by side, in metres. Zero is a true bench, with the desks touching. */
  readonly gapMetres: number;
  /** Between rows that are not back to back, in metres. */
  readonly aisleMetres: number;
  /** Between the backs of rows that are back to back, in metres. Zero is a true bench. */
  readonly backGapMetres: number;
  readonly onGapChange: (metres: number) => void;
  readonly onAisleChange: (metres: number) => void;
  readonly onBackGapChange: (metres: number) => void;
  /** What newly created desks and furniture are drawn as, whether stamped or placed one at a time. */
  readonly deskIcons: DeskIcons;
  readonly onDeskIconsChange: (icons: DeskIcons) => void;
  readonly onKindChange: (kind: DeskGroupKind) => void;
  readonly onCustomChange: (custom: DeskGroupShape) => void;
  readonly onFootprintChange: (footprint: DeskFootprint) => void;
  readonly onRotationChange: (degrees: number) => void;
  readonly onClose: () => void;
}

/**
 * Choosing what a desk stamp puts down.
 *
 * A bench of four, a row of three, a pair back to back: the arrangements offices are actually
 * laid out in. The alternative is placing twelve desks by hand, which costs twelve room
 * choices and leaves the spacing to whatever the mouse did.
 *
 * Every figure is in metres, so nothing here works on a plan nobody has measured — and it
 * says so rather than stamping desks at a size that means nothing.
 */
const DeskGroupPicker = ({
  anchorEl,
  scale,
  kind,
  custom,
  deskIcons,
  onDeskIconsChange,
  footprint,
  rotationDegrees,
  gapMetres,
  aisleMetres,
  backGapMetres,
  onGapChange,
  onAisleChange,
  onBackGapChange,
  onKindChange,
  onCustomChange,
  onFootprintChange,
  onRotationChange,
  onClose,
}: Props): React.JSX.Element => {
  const { t } = useTranslation();
  const { backToBack } = shapeOf(kind, custom);

  return (
    // A Popper rather than a Popover, which is a Modal: its invisible backdrop swallowed the
    // next click anywhere on the page. After choosing an arrangement, the click on the plan
    // meant to place it only dismissed this panel — nothing appeared, and there was no other
    // way to put the group down. A Popper renders no backdrop, so that click reaches the
    // canvas and places the desks, and the click-away closes the panel on its way past.
    <Popper
      open={anchorEl !== null}
      anchorEl={anchorEl}
      placement="right-start"
      sx={{ zIndex: (theme) => theme.zIndex.modal }}
    >
      <ClickAwayListener
        // Listening on mousedown rather than click, which is what closed this panel the moment
        // the arrangement dropdown was touched. A MUI Select opens its menu on mousedown, and
        // that menu mounts a full-screen backdrop straight away; the mouseup then lands on the
        // backdrop instead of the select. A click event fires on the common ancestor of its
        // mousedown and mouseup targets, so the click arrived here targeting <body> — outside
        // the panel by any test, and outside the menu too, which is why guarding on the target
        // alone was not enough. At mousedown the target is still the select itself.
        mouseEvent="onMouseDown"
        touchEvent="onTouchStart"
        onClickAway={(event): void => {
          // A click inside another floating layer is not a click away. The arrangement select,
          // the icon picker and every MUI menu render into a portal at the end of the body, so
          // this listener sees a press on one of their options as landing outside the panel —
          // and closing on it meant the panel vanished the moment its own dropdown was used.
          const target = event.target as HTMLElement | null;

          if (target?.closest?.('.MuiModal-root')) {
            return;
          }

          // And a press that reaches nothing recognisable while one of those layers is open is
          // that layer's, whatever it landed on: the icon picker's backdrop covers the page, so
          // nothing outside it can be clicked anyway. Closing on it would dismiss the panel
          // underneath the dropdown the admin is still using.
          if (document.querySelector('.MuiModal-root')) {
            return;
          }

          onClose();
        }}
      >
        <Paper elevation={8} sx={{ borderRadius: `${editorTheme.radius}px` }}>
          <Stack spacing={2} sx={{ p: 2, width: 300 }}>
            <Typography sx={sectionHeadingSx}>{t('floorplan-editor-desk-group')}</Typography>

            {!isUsableScale(scale) && (
            <Alert severity="info" sx={{ py: 0 }}>{t('floorplan-editor-desk-group-needs-scale')}</Alert>
            )}

            <TextField
              select
              size="small"
              value={kind}
              onChange={(event): void => onKindChange(event.target.value as DeskGroupKind)}
              label={String(t('floorplan-editor-desk-group-arrangement'))}
              // Left portalled. Rendering the menu inside this panel instead sounds tidier and
              // is worse: the menu's own Modal marks everything around it `aria-hidden`, and
              // inside the panel that includes the menu — which then exists for the mouse and
              // not for a screen reader. The click-away listener is told to ignore it instead.
            >
              {DESK_GROUP_KINDS.map((option) => (
                <MenuItem key={option} value={option}>
                  {t(`floorplan-editor-desk-group-${option}`, { count: seatCountOf(option, custom) })}
                </MenuItem>
              ))}
            </TextField>

            {/* Rows and columns, not a seat count: eight desks is a row of eight, two benches of
            four or a 4×2 block, and only the person laying out the floor knows which. */}
            {kind === 'custom' && (
            <Stack spacing={1}>
              <Stack direction="row" spacing={1}>
                <TextField
                  size="small"
                  type="number"
                  value={custom.columns}
                  onChange={(event): void => onCustomChange(
                    shapeOf('custom', { ...custom, columns: Number(event.target.value) }),
                  )}
                  label={String(t('floorplan-editor-desk-group-columns'))}
                  inputProps={{ min: 1, max: MAXIMUM_CUSTOM_DESKS, step: 1 }}
                />
                <TextField
                  size="small"
                  type="number"
                  value={custom.rows}
                  onChange={(event): void => onCustomChange(
                    shapeOf('custom', { ...custom, rows: Number(event.target.value) }),
                  )}
                  label={String(t('floorplan-editor-desk-group-rows'))}
                  inputProps={{ min: 1, max: MAXIMUM_CUSTOM_DESKS, step: 1 }}
                />
              </Stack>

              <ToggleButtonGroup
                exclusive
                size="small"
                value={custom.backToBack}
                onChange={(_, next: boolean | null): void => {
                  if (next !== null) {
                    onCustomChange({ ...custom, backToBack: next });
                  }
                }}
              >
                <ToggleButton value={false} aria-label={String(t('floorplan-editor-desk-group-aisles'))}>
                  <Typography variant="caption">{t('floorplan-editor-desk-group-aisles')}</Typography>
                </ToggleButton>
                <ToggleButton value aria-label={String(t('floorplan-editor-desk-group-back-to-back'))}>
                  <Typography variant="caption">{t('floorplan-editor-desk-group-back-to-back')}</Typography>
                </ToggleButton>
              </ToggleButtonGroup>

              <Typography variant="caption" color={StyleConstants.GREY_COLOR}>
                {t('floorplan-editor-desk-group-seats', { count: seatCountOf('custom', custom) })}
              </Typography>
            </Stack>
            )}

            <Divider />

            {/* Defaults rather than a property of this stamp: they apply to a desk placed one at a
            time as well, which is the only way "make every desk a phone booth" is one action
            instead of forty. */}
            <DeskIconField
              labelKey="floorplan-editor-desk-icon-default"
              iconName={deskIcons.desk}
              onChange={(iconName): void => onDeskIconsChange({ ...deskIcons, desk: iconName })}
            />

            <DeskIconField
              kind="furniture"
              labelKey="floorplan-editor-desk-shape-icon-default"
              iconName={deskIcons.shape}
              onChange={(iconName): void => onDeskIconsChange({ ...deskIcons, shape: iconName })}
            />

            <Divider />

            <Typography variant="caption" color={StyleConstants.GREY_COLOR}>
              {t('floorplan-editor-desk-size')}
            </Typography>

            <Stack direction="row" spacing={1}>
              <TextField
                size="small"
                type="number"
                value={footprint.widthMetres}
                onChange={(event): void => {
                  const width = Number(event.target.value);

                  if (Number.isFinite(width) && width > 0) {
                    onFootprintChange({ ...footprint, widthMetres: width });
                  }
                }}
                label={String(t('floorplan-editor-desk-width'))}
                inputProps={{ min: 0.3, step: 0.1 }}
              />
              <TextField
                size="small"
                type="number"
                value={footprint.depthMetres}
                onChange={(event): void => {
                  const depth = Number(event.target.value);

                  if (Number.isFinite(depth) && depth > 0) {
                    onFootprintChange({ ...footprint, depthMetres: depth });
                  }
                }}
                label={String(t('floorplan-editor-desk-depth'))}
                inputProps={{ min: 0.3, step: 0.1 }}
              />
            </Stack>

            {/* How far apart the desks sit. The defaults are a working office's, not a
                warehouse's, and both are worth changing before stamping a floor's worth. */}
            <Stack direction="row" spacing={1}>
              <TextField
                size="small"
                type="number"
                value={gapMetres}
                onChange={(event): void => {
                  const metres = Number(event.target.value);

                  if (Number.isFinite(metres) && metres >= 0 && metres <= MAXIMUM_SPACING_METRES) {
                    onGapChange(metres);
                  }
                }}
                label={String(t('floorplan-editor-desk-group-gap'))}
                inputProps={{ min: 0, max: MAXIMUM_SPACING_METRES, step: 0.1 }}
              />
              {/* One field, two things, because between two rows of desks there is only ever
                  one distance — what it is called depends on what is on the other side of it.
                  An aisle is walked down; the strip between two desks' backs holds cables, a
                  screen rail or a planter, and is usually nothing at all. The field used to be
                  disabled outright for a bench, which left the rows welded together. */}
              <TextField
                size="small"
                type="number"
                value={backToBack ? backGapMetres : aisleMetres}
                onChange={(event): void => {
                  const metres = Number(event.target.value);

                  if (Number.isFinite(metres) && metres >= 0 && metres <= MAXIMUM_SPACING_METRES) {
                    (backToBack ? onBackGapChange : onAisleChange)(metres);
                  }
                }}
                label={String(t(backToBack
                  ? 'floorplan-editor-desk-group-back-gap'
                  : 'floorplan-editor-desk-group-aisle'))}
                inputProps={{ min: 0, max: MAXIMUM_SPACING_METRES, step: 0.1 }}
                helperText={backToBack
                  ? String(t('floorplan-editor-desk-group-back-gap-hint'))
                  : undefined}
              />
            </Stack>

            <TextField
              size="small"
              type="number"
              value={rotationDegrees}
              onChange={(event): void => {
                const degrees = Number(event.target.value);

                if (Number.isFinite(degrees)) {
                  onRotationChange(degrees);
                }
              }}
              label={String(t('floorplan-editor-desk-group-rotation'))}
              inputProps={{ step: 15 }}
              helperText={String(t('floorplan-editor-desk-group-rotation-hint'))}
            />

            <Divider />

            {/* Says what to do next, because nothing else does: the panel is open, the tool is
            armed, and the only remaining step is a click on the plan. */}
            <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
              <Typography variant="caption" color={StyleConstants.GREY_COLOR}>
                {t('floorplan-editor-desk-group-place-hint')}
              </Typography>
              <Button size="small" onClick={onClose} sx={{ textTransform: 'none' }}>
                {t('floorplan-editor-desk-group-done')}
              </Button>
            </Stack>
          </Stack>
        </Paper>
      </ClickAwayListener>
    </Popper>
  );
};

export default DeskGroupPicker;
